import React, { useEffect, useState, useMemo } from "react";
import { object } from "prop-types";
import ConferenceStoryCard from "../../../molecules/ConferenceStoryCard";
import LoadingIndicatorComponent from "../../../atoms/loading-indicator";
import "./events-tab.m.css";

export const EventsTab = ({ memberData }) => {
  const { email = "" } = memberData;
  const [eventIds, setEventIds] = useState([]);
  const [eventStories, setEventStories] = useState([]);
  const [isLoadingIds, setIsLoadingIds] = useState(true);
  const [isLoadingStories, setIsLoadingStories] = useState(false);
  const [visiblePreviousCount, setVisiblePreviousCount] = useState(6);
  const classMap = {
    upcoming: {
      conference: 'upcoming-conferences-section',
      workshop: 'upcoming-workshops-section',
      session: 'upcoming-sessions-section'
    },
    previous: {
      conference: 'previous-conferences-section',
      workshop: 'previous-workshops-section',
      session: 'previous-sessions-section'
    }
  };

  useEffect(() => {
    const fetchEventIds = async () => {
      setIsLoadingIds(true);
      try {
        const url = "/api/v1/entities?type=event-participants";
        const response = await fetch(url);
        const data = await response.json();
        const allParticipantEntities = data?.entities;
        let userEvents = [];
        allParticipantEntities?.map((entity) => {
          const isEmailFound = entity?.participants?.some(participant => participant?.name === email);
          isEmailFound && entity?.["story-id"] && userEvents?.push(entity?.["story-id"]);
        });
        setEventIds(userEvents);
      } catch (error) {
        console.error('Error fetching event participants:', error);
        setEventIds([]);
      } finally {
        setIsLoadingIds(false);
      }
    };

    if (email) {
      fetchEventIds();
    } else {
      setIsLoadingIds(false);
    }
  }, [email]);

  useEffect(() => {
    const fetchAllStories = async () => {
      if (!eventIds || eventIds.length === 0) {
        setEventStories([]);
        setIsLoadingStories(false);
        return;
      }

      setIsLoadingStories(true);
      try {
        const fetchStory = async (id) => {
          const url = `/api/v1/stories/${id}`;
          const response = await fetch(url);
          const data = await response.json();
          return data?.story || data;
        };

        // Use Promise.all to fetch all stories concurrently
        const storyPromises = eventIds.map(id => fetchStory(id));
        const fetchedStories = await Promise.all(storyPromises);

        // Filter out null/undefined results
        const validStories = fetchedStories.filter(story => story);

        setEventStories(validStories);
      } catch (error) {
        console.error('Error fetching event stories:', error);
        setEventStories([]);
      } finally {
        setIsLoadingStories(false);
      }
    };

    fetchAllStories();
  }, [eventIds]);

  // Filter stories into current and previous registrations
  const { currentRegistrations, previousRegistrations } = useMemo(() => {
    const current = [];
    const previous = [];

    eventStories.forEach(story => {
      const storyAttributes = story?.metadata?.['story-attributes'] || {};
      const isPreviousEvent = storyAttributes?.ispreviousevent?.[0] === "true";

      if (isPreviousEvent) {
        previous.push(story);
      } else {
        current.push(story);
      }
    });

    return { currentRegistrations: current, previousRegistrations: previous };
  }, [eventStories]);

  // Show loading indicator while fetching data
  if (isLoadingIds || isLoadingStories) {
    return (
      <div styleName="events-tab">
        <LoadingIndicatorComponent />
      </div>
    );
  }

  // Show empty state only after loading is complete and no events are found
  if (eventStories.length === 0) {
    return (
      <div styleName="events-tab">
        <div styleName="empty-state">
          <p>No events found for your account.</p>
        </div>
      </div>
    );
  }

  // Helper function to get CSS class based on story template and variant
  const getEventTypeClass = (story, variant) => {
    const template = story?.['story-template'] || 'conference';
    return classMap[variant][template];
  };

  // Handle load more for previous registrations
  const handleLoadMore = () => {
    setVisiblePreviousCount(prevCount => prevCount + 6);
  };

  // Get visible previous registrations
  const visiblePreviousRegistrations = previousRegistrations.slice(0, visiblePreviousCount);
  const hasMorePrevious = previousRegistrations.length > visiblePreviousCount;

  return (
    <div styleName="events-tab">
      {/* Current Registrations Section */}
      {currentRegistrations.length > 0 && (
        <div styleName="events-section">
          <div styleName="section-header">
            <h2 styleName="section-title">Currently registered</h2>
          </div>
          <div styleName="events-grid">
            {currentRegistrations.map((story) => (
              <div key={story.id} styleName={getEventTypeClass(story, 'upcoming')}>
                <ConferenceStoryCard
                  story={story}
                  variant="upcoming"
                  showEventName={true}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Previous Registrations Section */}
      {previousRegistrations.length > 0 && (
        <div styleName="events-section">
          <div styleName="section-header">
            <h2 styleName="section-title">Previous registrations</h2>
          </div>
          <div styleName="events-grid">
            {visiblePreviousRegistrations.map((story) => (
              <div key={story.id} styleName={getEventTypeClass(story, 'previous')}>
                <ConferenceStoryCard
                  story={story}
                  variant="previous"
                  showEventName={true}
                />
              </div>
            ))}
          </div>
          {hasMorePrevious && (
            <div styleName="load-more-container">
              <button styleName="load-more-button" onClick={handleLoadMore}>
                Load more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

EventsTab.propTypes = {
  memberData: object.isRequired,
};